import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2Icon, SearchIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { InvoiceStatusBadge } from '../../components/admin/InvoiceStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate } from '../../utils/format';
import { formatKr } from '../../utils/quote';
import { isOverdue } from '../../utils/invoice';
type Filter = 'Alle' | 'Kladder' | 'Sendt' | 'Betalt' | 'Forfalden';
const filters: Filter[] = ['Alle', 'Kladder', 'Sendt', 'Betalt', 'Forfalden'];
export function Invoices() {
  const {
    invoices,
    cases,
    dataLoading
  } = useAdminData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('Alle');
  const rows = useMemo(() => invoices.map(invoice => ({
    invoice,
    overdue: isOverdue(invoice),
    linkedCase: cases.find(item => item.id === invoice.caseId)
  })), [cases, invoices]);
  const summary = useMemo(() => ({
    drafts: rows.filter(({
      invoice
    }) => invoice.status === 'Kladde').length,
    ready: rows.filter(({
      invoice
    }) => invoice.status === 'Klar til afsendelse').length,
    sent: rows.filter(({
      invoice
    }) => invoice.status === 'Sendt').length,
    paid: rows.filter(({
      invoice
    }) => invoice.status === 'Betalt').length,
    overdue: rows.filter(({
      overdue
    }) => overdue).length
  }), [rows]);
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return rows.filter(({
      invoice,
      overdue
    }) => {
      const matchesFilter = filter === 'Alle' || filter === 'Kladder' && invoice.status === 'Kladde' || filter === 'Sendt' && invoice.status === 'Sendt' || filter === 'Betalt' && invoice.status === 'Betalt' || filter === 'Forfalden' && overdue;
      const haystack = `${invoice.invoiceNumber} ${invoice.customerName} ${invoice.reference}`.toLowerCase();
      return matchesFilter && (!term || haystack.includes(term));
    });
  }, [filter, query, rows]);
  const cards = [{
    label: 'Kladder',
    value: summary.drafts
  }, {
    label: 'Klar til afsendelse',
    value: summary.ready
  }, {
    label: 'Sendt',
    value: summary.sent
  }, {
    label: 'Betalt',
    value: summary.paid
  }, {
    label: 'Forfalden',
    value: summary.overdue
  }];
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1463" data-mp="1mg7yjmengq2l">
      <div data-id="element-1464" data-mp="1mj3u5430z2nv">
        <p className="text-sm text-ink-muted" data-id="element-1465" data-mp="1miu117moofdy">{invoices.length} fakturaer i alt</p>
        <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1466" data-mp="1hk0pvluvd0gy">
          Fakturaer
        </h1>
        <p className="mt-2 max-w-xl text-[15px] text-ink-soft" data-id="element-1467" data-mp="1rh5dzr89z60e">
          Fakturaer oprettes fra en sag med knappen «Opret faktura», når arbejdet er udført.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-id="element-1468" data-mp="3t80bcni4rd98">
        {cards.map(card => <div key={card.label} className="rounded-card border border-line bg-white p-4" data-id="element-1469" data-mp="28p6ddn2a7exq">
            <p className="font-display text-2xl font-bold tracking-tight text-ink" data-id="element-1470" data-mp="2doso9pun0j3d">{card.value}</p>
            <p className="mt-1 text-sm text-ink-muted" data-id="element-1471" data-mp="1z0endkgs74pg">{card.label}</p>
          </div>)}
      </div>

      <div className="mt-6" data-id="element-1472" data-mp="23xo9aq4yoy8j">
        <label htmlFor="fakturasoegning" className="sr-only" data-id="element-1473" data-mp="23wvbienzdj2j">
          Søg i fakturaer
        </label>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3" data-id="element-1474" data-mp="1pxn9le0fnwun">
          <SearchIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-1475" data-mp="1u1v1fk26tsx3" />
          <input id="fakturasoegning" value={query} onChange={event => setQuery(event.target.value)} placeholder="Søg på fakturanummer, kunde eller reference" className="w-full bg-transparent text-[15px] text-ink placeholder:text-ink-faint focus:outline-none" data-id="element-1476" data-mp="1g2bugul44nmd" />
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filtrér fakturaer" data-id="element-1477" data-mp="1fbdbvgcexzvk">
          {filters.map(option => {
          const active = filter === option;
          return <button key={option} type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors duration-200 ease-smooth ${active ? 'border-accent/50 bg-accent/15 text-ink' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-1478" data-mp="169dxywwp3bwu">
                {option}
              </button>;
        })}
        </div>
      </div>

      {dataLoading && invoices.length === 0 && <p className="mt-8 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-1479" data-mp="8mn93zn9qj3a">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1480" data-mp="2kaim6ixld5js" />
          Henter fakturaer …
        </p>}

      <div className="mt-6 rounded-card border border-line bg-white" data-id="element-1481" data-mp="2p8txky3jbo44">
        <div className="hidden md:block" data-id="element-1482" data-mp="2u8q1kxc8ffjo">
          <table className="w-full text-left text-[15px]" data-id="element-1483" data-mp="2u9rez8gq0e4k">
            <thead data-id="element-1484" data-mp="2yab7tzu9gvkw">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-1485" data-mp="2yatc9jr2ug3w">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1486" data-mp="21ktvj20rwba8">Fakturanummer</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1487" data-mp="26ivoceo03oyw">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1488" data-mp="26grc99fy7jnj">Dato</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1489" data-mp="2aj5srmw5a7bn">Forfaldsdato</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1490" data-mp="2fc8sydvfgf0t">Beløb</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1491" data-mp="1iucie5sfbo9z">Status</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1492" data-mp="1mv4pasdtph33">Tilknyttet sag</th>
                <th scope="col" className="px-5 py-3 text-right font-medium" data-id="element-1493" data-mp="1mss2a0t7kgj6">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1494" data-mp="2l7h2oyruxbdk">
              {visible.map(({
              invoice,
              overdue,
              linkedCase
            }) => <tr key={invoice.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-1495" data-mp="2zutsizop639r">
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-1496" data-mp="276xxs0ioulfm">
                    {invoice.invoiceNumber}
                  </td>
                  <td className="px-5 py-3.5 text-ink" data-id="element-1497" data-mp="229o1c0wvt9ia">{invoice.customerName || '—'}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1498" data-mp="2g8nmuzp3l8a8">
                    {formatDate(invoice.invoiceDate)}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1499" data-mp="3rmrh86zlkyft">
                    {formatDate(invoice.dueDate)}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-1500" data-mp="3nkwfj1dr8j1i">
                    {formatKr(invoice.total)}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-1501" data-mp="3z718k0et8g3">
                    <InvoiceStatusBadge status={overdue ? 'Forfalden' : invoice.status} data-id="element-1502" data-mp="408m1g7899b0" />
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1503" data-mp="3l8vp8djc4mfy">
                    {linkedCase ? <Link to={`/admin/sager/${linkedCase.id}`} className="font-medium text-accent hover:text-accent-deep" data-id="element-1504" data-mp="hq2ee8hxybph">
                        Sag #{linkedCase.number}
                      </Link> : '—'}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-1505" data-mp="cxj17cn61il7">
                    <Link to={`/admin/fakturaer/${invoice.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-1506" data-mp="10oofhndgk2te">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-1507" data-mp="2wqhzmt7wyt7k">
          {visible.map(({
          invoice,
          overdue
        }) => <li key={invoice.id} data-id="element-1508" data-mp="2sqpu6pdth36f">
              <Link to={`/admin/fakturaer/${invoice.id}`} className="block px-5 py-4" data-id="element-1509" data-mp="2nrmxptbwk5a2">
                <div className="flex items-start justify-between gap-3" data-id="element-1510" data-mp="3khng9alohpix">
                  <div className="min-w-0" data-id="element-1511" data-mp="3kgugwup1u2at">
                    <p className="text-xs text-ink-faint" data-id="element-1512" data-mp="3gfisgshwvn47">{invoice.invoiceNumber}</p>
                    <p className="mt-1 truncate text-[15px] font-medium text-ink" data-id="element-1513" data-mp="3bgpbur7bvfzt">
                      {invoice.customerName || '—'}
                    </p>
                  </div>
                  <InvoiceStatusBadge status={overdue ? 'Forfalden' : invoice.status} data-id="element-1514" data-mp="36k8mjtoe6vl5" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-1515" data-mp="1v2pftrblsahu">
                  {formatDate(invoice.invoiceDate)} · forfald {formatDate(invoice.dueDate)} ·{' '}
                  {formatKr(invoice.total)}
                </p>
              </Link>
            </li>)}
        </ul>

        {!dataLoading && visible.length === 0 && <p className="px-5 py-12 text-center text-sm text-ink-muted" data-id="element-1516" data-mp="1uvx7ypkqpxe5">
            Ingen fakturaer matcher søgningen.
          </p>}
      </div>
    </div>;
}